import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { toApiError, useApi } from '@/shared/api';
import type { AriPushRequest } from '@/shared/api';
import { addTrackedAriOperations } from './ariOperationStorage';
import {
  isMissingAtChannex,
  isNotRoutable,
  MISSING_AT_CHANNEX,
  NOT_ROUTABLE,
} from './ariPushErrors';

interface UseAriPushArgs {
  onSuccess: () => void;
}

function summarize(req: AriPushRequest): string {
  const parts: string[] = [];
  if (req.rate != null) parts.push('Preis');
  if (req.available != null) parts.push('Verfügbarkeit');
  if (req.minStay != null) parts.push('Mindestaufenthalt');
  if (req.maxStay != null) parts.push('Höchstaufenthalt');
  if (req.closedToArrival != null) parts.push('Anreisesperre');
  if (req.closedToDeparture != null) parts.push('Abreisesperre');
  if (req.stopSell != null) parts.push('Verkaufsstopp');
  return parts.join(' + ') || 'Änderung';
}

export function useAriPush({ onSuccess }: UseAriPushArgs) {
  const api = useApi();
  return useMutation({
    mutationFn: (body: AriPushRequest) => api.channex.pushAri(body),
    onSuccess: (result, variables) => {
      addTrackedAriOperations(result.propertyId, [result.operationId]);
      toast.success(
        `${summarize(variables)} für ${result.daysSet} Tage vorgemerkt — der Status wird hier verfolgt`,
      );
      onSuccess();
    },
    onError: async (err) => {
      const e = await toApiError(err);
      const msg = [e.title, e.detail].filter(Boolean).join(' — ');
      if (isNotRoutable(e)) {
        toast.error(NOT_ROUTABLE, { description: msg, duration: 15000 });
      } else if (isMissingAtChannex(e)) {
        toast.error(MISSING_AT_CHANNEX, { description: msg, duration: 15000 });
      } else {
        toast.error(msg || 'ARI-Push fehlgeschlagen.', { duration: 8000 });
      }
    },
  });
}
